import React, { Fragment, useContext, useState, useEffect } from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import moment from 'moment-timezone';
import {
  DATE_TIME_PICKER_SUGGEST_BOX_WIDTH_PX,
  ReactDatePicker,
  ReactDateTimePicker,
  SuggestBox,
} from '@mummssoftware/common-ui';
import { momentToDate } from '@mummssoftware/utils/dates';
import { useStyles } from '@mummssoftware/common-ui/VisitAttributeSelector';
import { Deceased } from '@mummssoftware/utils/constants';
import {
  GlobalContext,
  attemptedVisit,
} from '@mummssoftware/global-context/GlobalContext';
import { VisitContext } from '../Context/contexts/VisitContext';
import { Context } from '../../context';
import {
  IntroVisitDiv,
  VisitIntroLabeledRow,
  VisitIntroStack,
  SmallDiv,
  TextDiv,
  VisitInterfaceDiv,
} from '../../components';
import { SET_ACTING_AS_ROLE } from '../Context/constants';
import defaults from '../../translations/en.json';

type VisitIntroProps = {
  siteTimezone: any;
  roleTypeOption: any;
  setRoleTypeOption: any;
  setLocationTypeOption: any;
  setVisitDate: any;
  visitDate: any;
  locationTypeOption: any;
};

export type OptionVisitType = {
  visittype: string;
  type: string;
  billable: string;
  label: string;
  value: string;
  id: string;
};

export const VisitIntro = ({
  siteTimezone,
  roleTypeOption,
  setRoleTypeOption,
  setLocationTypeOption,
  setVisitDate,
  visitDate,
  locationTypeOption,
}: VisitIntroProps) => {
  const classes = useStyles();
  const isNarrowScreen = useMediaQuery('(max-width:600px)');
  const { currentUser, patient } = useContext(Context);
  const {
    dispatchVisit,
    roleGroups,
    IPUVisit,
    is30MinAgency,
    visitTypeOptions,
    visitEpisode,
  } = useContext(VisitContext);
  const { levelOfCare } = useContext(GlobalContext);

  const isAttemptedVisit = levelOfCare === attemptedVisit;
  const showOnlyDate = IPUVisit || isAttemptedVisit;

  const [displayDate, setDisplayDate] = useState(null);

  const nurseTitle =
    currentUser && `${currentUser?.firstName} ${currentUser?.lastName}`;
  /**
   * Patient with multiples episodes
   * Need Referral and discharge dates
   */
  const isPatientDD = patient?.currentStatus === Deceased;
  const referralDate =
    patient && new Date((patient as mumms.patient).referralDate);
  const episodeReferralDate =
    visitEpisode &&
    visitEpisode.utcReferralDate &&
    moment.tz(visitEpisode.utcReferralDate, siteTimezone);
  const episodeDischargeDate =
    visitEpisode &&
    visitEpisode.utcDischargeDate &&
    moment.tz(visitEpisode.utcDischargeDate, siteTimezone);
  const minDate = episodeReferralDate || moment(referralDate);
  const DDrule = isPatientDD && visitEpisode?.isLast;
  const maxDate =
    episodeDischargeDate && !DDrule
      ? episodeDischargeDate?.toDate()
      : new Date();
  /**
   * setVisitDate is set in START VISIT, need to adjust
   */
  useEffect(() => {
    // OP-33239: an IPU visit always starts at the current date, so keep nowDate from StartVisit.
    if (!IPUVisit && visitEpisode && episodeDischargeDate) {
      setVisitDate(maxDate);
    }
  }, [visitEpisode]);

  const onRoleTypeChange = (role: any) => {
    dispatchVisit({
      type: SET_ACTING_AS_ROLE,
      payload: role.rolename,
    });

    setRoleTypeOption(role);
  };

  const onLocationTypeChange = (location: any) => {
    setLocationTypeOption(location);
  };

  const handleDateChange = (visitdate: any) => {
    setVisitDate(visitdate);
    setDisplayDate(visitdate);
  };

  const pickerSelectedDate = () => {
    const time = moment.tz(maxDate, moment.tz.guess()).tz(siteTimezone);
    return displayDate ? moment(displayDate).toDate() : momentToDate(time);
  };

  if (showOnlyDate) {
    return IPUVisit ? (
      <Fragment>
        <VisitInterfaceDiv id="ipu-visit-date">
          <TextDiv>{defaults.visitDate}</TextDiv>
          {/* OP-33239: an IPU visit always starts now; the date is read-only */}
          <ReactDatePicker
            dateFormat="MM-dd-yy"
            inputMargin="0 0 4px 0"
            inputWidth="125px"
            selectedDate={momentToDate(moment(visitDate).tz(siteTimezone))}
            onChange={() => undefined}
            disabled
            readOnly
          />
        </VisitInterfaceDiv>
      </Fragment>
    ) : (
      <Fragment>
        <VisitInterfaceDiv>
          <TextDiv>{defaults.visitDate}</TextDiv>
          <ReactDateTimePicker
            inputMargin="0 0 4px 0"
            dateFormat="MM-dd-yy h:mm aa"
            inputWidth={`${DATE_TIME_PICKER_SUGGEST_BOX_WIDTH_PX}px`}
            selectedDate={pickerSelectedDate()}
            onChange={handleDateChange}
            minimumDate={minDate && (minDate as any).toDate()}
            maximumDate={maxDate}
            timeIntervals={is30MinAgency ? 30 : 15}
          />
        </VisitInterfaceDiv>
      </Fragment>
    );
  }

  const getMobileUI = () => (
    <Fragment>
      <IntroVisitDiv id="intro-1-visit">
        <VisitIntroStack>
          <VisitIntroLabeledRow>
            <TextDiv>{`${nurseTitle}, ${defaults.actingInCapacity}`}</TextDiv>
            <SmallDiv align="left" width={190}>
              <SuggestBox
                disabled={!(roleGroups && roleGroups.length > 1)}
                options={roleGroups || []}
                value={roleTypeOption}
                onChange={onRoleTypeChange}
                className={classes.inputBase}
                noMaxHeight
                maxMenuHeight={280}
                compactMenuItems
              />
            </SmallDiv>
          </VisitIntroLabeledRow>
        </VisitIntroStack>
      </IntroVisitDiv>
      <IntroVisitDiv id="intro-2-visit">
        <VisitIntroStack>
          <VisitIntroLabeledRow>
            <TextDiv>{defaults.visitDate}</TextDiv>
            <ReactDatePicker
              dateFormat="MM-dd-yy"
              inputMargin="0 0 4px 0"
              inputWidth="100%"
              inputTextAlign="left"
              selectedDate={pickerSelectedDate()}
              minimumDate={minDate && (minDate as any).toDate()}
              maximumDate={maxDate}
              onChange={handleDateChange}
              timeIntervals={is30MinAgency ? 30 : 15}
            />
          </VisitIntroLabeledRow>
          <VisitIntroLabeledRow>
            <TextDiv>{defaults.visitType}</TextDiv>
            <SmallDiv align="left" width={190}>
              <SuggestBox
                options={visitTypeOptions || []}
                value={locationTypeOption}
                onChange={onLocationTypeChange}
                className={classes.inputBase}
                noMaxHeight
                maxMenuHeight={280}
                compactMenuItems
              />
            </SmallDiv>
          </VisitIntroLabeledRow>
        </VisitIntroStack>
      </IntroVisitDiv>
    </Fragment>
  );

  const getDesktopUI = () => (
    <Fragment>
      <IntroVisitDiv id="intro-1-visit">
        <TextDiv>{`${nurseTitle}, ${defaults.actingInCapacity}`}</TextDiv>
        <SmallDiv align="left" width={190}>
          <SuggestBox
            fitcontent
            disabled={!(roleGroups && roleGroups.length > 1)}
            options={roleGroups || []}
            value={roleTypeOption}
            onChange={onRoleTypeChange}
            className={classes.inputBase}
            noMaxHeight
            maxMenuHeight={300}
          />
        </SmallDiv>
      </IntroVisitDiv>
      <IntroVisitDiv id="intro-2-visit">
        <VisitInterfaceDiv id="intro-sub-visit">
          <TextDiv>{defaults.visitDate}</TextDiv>
          <ReactDatePicker
            dateFormat="MM-dd-yy"
            inputMargin="0 0 4px 0"
            inputWidth="125px"
            selectedDate={pickerSelectedDate()}
            minimumDate={minDate && (minDate as any).toDate()}
            maximumDate={maxDate}
            // filterTime={.  ---- removed because this was when the component was dateTimePicker
            //   (time: Date) => preventFutureTimesMoment(dateToMoment(time)) /**/
            // }
            onChange={handleDateChange}
            timeIntervals={is30MinAgency ? 30 : 15}
          />
          <TextDiv>{defaults.visitType}</TextDiv>
          <SmallDiv align="left" width={180}>
            <SuggestBox
              fitcontent
              options={visitTypeOptions || []}
              value={locationTypeOption}
              onChange={onLocationTypeChange}
              className={classes.inputBase}
              noMaxHeight
              maxMenuHeight={300}
            />
          </SmallDiv>
        </VisitInterfaceDiv>
      </IntroVisitDiv>
    </Fragment>
  );

  return isNarrowScreen ? getMobileUI() : getDesktopUI();
};
